import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Save, RefreshCw, Palette, Type, Globe, Share2, Sparkles, Heart, Upload as UploadIcon, ImageIcon } from "lucide-react";

const ColorInput = ({ label, value, onChange, testid }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm border border-border" style={{ background: `hsl(${value || "0 0% 100%"})` }} />
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder='e.g. "205 40% 40%"' data-testid={testid}
                className="flex-1 px-3 py-2 rounded-sm border border-border bg-background text-sm font-mono" />
        </div>
    </label>
);
const Field = ({ label, value, onChange, testid, type = "text" }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" />
    </label>
);
const Area = ({ label, value, onChange, testid, rows = 3 }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" />
    </label>
);

const PRESETS = [
    { name: "Default Earth", primary: "205 40% 40%", secondary: "140 25% 42%", accent: "200 50% 90%", background: "40 30% 96%" },
    { name: "Soft Green", primary: "150 35% 35%", secondary: "200 35% 45%", accent: "150 40% 90%", background: "60 25% 96%" },
    { name: "Sahel Sand", primary: "30 50% 38%", secondary: "190 30% 40%", accent: "35 60% 90%", background: "40 40% 96%" },
    { name: "Calm Sky", primary: "210 50% 45%", secondary: "180 35% 42%", accent: "200 55% 92%", background: "210 30% 97%" },
];

export default function Settings() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    useEffect(() => { api.get("/config").then(({ data }) => setCfg(data)); }, []);

    const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/config", cfg);
            setCfg(data);
            window.location.reload();
        } finally { setSaving(false); }
    };
    const applyPreset = (p) => {
        setCfg((c) => ({ ...c, color_primary: p.primary, color_secondary: p.secondary, color_accent: p.accent, color_background: p.background }));
    };

    if (!cfg) return <div className="text-muted-foreground">Loading…</div>;

    return (
        <div className="max-w-5xl space-y-10" data-testid="settings-page">
            <div>
                <h1 className="font-display text-4xl mb-2">Settings</h1>
                <p className="text-muted-foreground">Theme, branding, SEO, footer, social links, and animation behavior.</p>
            </div>

            {/* Branding */}
            <Section title="Branding" icon={Globe}>
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Site name" value={cfg.site_name} onChange={(v) => set("site_name", v)} testid="setting-site-name" />
                    <Field label="Tagline" value={cfg.tagline} onChange={(v) => set("tagline", v)} testid="setting-tagline" />
                </div>
                <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Logo</span>
                    <div className="flex items-center gap-4 flex-wrap">
                        {cfg.logo_url ? (
                            <img src={fileUrl(cfg.logo_url)} alt="Logo" className="h-20 w-20 object-contain border border-border rounded-sm bg-background p-1" data-testid="logo-preview" />
                        ) : (
                            <div className="h-20 w-20 flex items-center justify-center border border-border rounded-sm text-muted-foreground"><ImageIcon size={20} /></div>
                        )}
                        <div className="flex-1 min-w-[240px] flex items-center gap-3">
                            <input value={cfg.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://… or /api/files/…"
                                className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="logo-url-input" />
                            <LogoUploader onUpload={(url) => set("logo_url", url)} />
                            {cfg.logo_url && <button type="button" onClick={() => set("logo_url", "")} className="px-3 py-2.5 rounded-sm border border-border text-xs text-muted-foreground hover:text-destructive" data-testid="logo-clear">Clear</button>}
                        </div>
                    </div>
                </div>
            </Section>

            {/* Theme */}
            <Section title="Theme & Colors" icon={Palette}>
                <div className="mb-6">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Presets</div>
                    <div className="flex flex-wrap gap-2">
                        {PRESETS.map((p) => (
                            <button key={p.name} onClick={() => applyPreset(p)}
                                className="px-4 py-2 text-sm rounded-sm border border-border hover:border-primary/60 transition-colors"
                                data-testid={`preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                {p.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <ColorInput label="Background" value={cfg.color_background} onChange={(v) => set("color_background", v)} testid="color-bg" />
                    <ColorInput label="Foreground" value={cfg.color_foreground} onChange={(v) => set("color_foreground", v)} testid="color-fg" />
                    <ColorInput label="Primary" value={cfg.color_primary} onChange={(v) => set("color_primary", v)} testid="color-primary" />
                    <ColorInput label="Secondary" value={cfg.color_secondary} onChange={(v) => set("color_secondary", v)} testid="color-secondary" />
                    <ColorInput label="Accent" value={cfg.color_accent} onChange={(v) => set("color_accent", v)} testid="color-accent" />
                    <ColorInput label="Muted" value={cfg.color_muted} onChange={(v) => set("color_muted", v)} testid="color-muted" />
                    <ColorInput label="Border" value={cfg.color_border} onChange={(v) => set("color_border", v)} testid="color-border" />
                    <ColorInput label="Footer background" value={cfg.color_footer_bg} onChange={(v) => set("color_footer_bg", v)} testid="color-footer-bg" />
                    <ColorInput label="Footer text" value={cfg.color_footer_text} onChange={(v) => set("color_footer_text", v)} testid="color-footer-text" />
                </div>
                <p className="text-xs text-muted-foreground mt-4">Use HSL format: <code>hue saturation% lightness%</code> (e.g., <code>205 40% 40%</code>).</p>
            </Section>

            {/* Typography */}
            <Section title="Typography" icon={Type}>
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Heading font family" value={cfg.font_heading} onChange={(v) => set("font_heading", v)} testid="font-heading" />
                    <Field label="Body font family" value={cfg.font_body} onChange={(v) => set("font_body", v)} testid="font-body" />
                </div>
                <Field label="Google Fonts URL" value={cfg.font_google_url} onChange={(v) => set("font_google_url", v)} testid="font-url" />
                <p className="text-xs text-muted-foreground">Example: <code>https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=Work+Sans:wght@300;400;500;700&display=swap</code></p>
            </Section>

            {/* SEO */}
            <Section title="SEO" icon={Globe}>
                <Field label="Site title" value={cfg.seo_title} onChange={(v) => set("seo_title", v)} testid="seo-title" />
                <Area label="Description" value={cfg.seo_description} onChange={(v) => set("seo_description", v)} testid="seo-desc" />
                <Field label="Keywords" value={cfg.seo_keywords} onChange={(v) => set("seo_keywords", v)} testid="seo-keywords" />
            </Section>

            {/* Footer */}
            <Section title="Footer & Contact" icon={Globe}>
                <Area label="Footer text" value={cfg.footer_text} onChange={(v) => set("footer_text", v)} testid="footer-text" />
                <div className="grid sm:grid-cols-3 gap-5">
                    <Field label="Contact email" value={cfg.contact_email} onChange={(v) => set("contact_email", v)} testid="contact-email-setting" />
                    <Field label="Contact phone" value={cfg.contact_phone} onChange={(v) => set("contact_phone", v)} testid="contact-phone-setting" />
                    <Field label="Contact address" value={cfg.contact_address} onChange={(v) => set("contact_address", v)} testid="contact-addr-setting" />
                </div>
            </Section>

            {/* Social */}
            <Section title="Social Media" icon={Share2}>
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Facebook URL" value={cfg.social_facebook} onChange={(v) => set("social_facebook", v)} testid="social-fb" />
                    <Field label="Twitter URL" value={cfg.social_twitter} onChange={(v) => set("social_twitter", v)} testid="social-tw" />
                    <Field label="Instagram URL" value={cfg.social_instagram} onChange={(v) => set("social_instagram", v)} testid="social-ig" />
                    <Field label="LinkedIn URL" value={cfg.social_linkedin} onChange={(v) => set("social_linkedin", v)} testid="social-li" />
                    <Field label="YouTube URL" value={cfg.social_youtube} onChange={(v) => set("social_youtube", v)} testid="social-yt" />
                </div>
            </Section>

            {/* Email */}
            <Section title="Email (Resend)" icon={Globe}>
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Sender email (must be on verified Resend domain)" value={cfg.sender_email} onChange={(v) => set("sender_email", v)} testid="sender-email" />
                    <Field label="Sender name" value={cfg.sender_name} onChange={(v) => set("sender_name", v)} testid="sender-name" />
                </div>
                <p className="text-xs text-muted-foreground">Verify your domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline text-primary">resend.com/domains</a> then set <code>RESEND_API_KEY</code> in <code>backend/.env</code>. Receipts will be sent automatically.</p>
            </Section>

            {/* Animation */}
            <Section title="Photo Animations" icon={Sparkles}>
                <div className="grid sm:grid-cols-2 gap-5">
                    <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Animation type</span>
                        <select value={cfg.animation_type || "fade"} onChange={(e) => set("animation_type", e.target.value)}
                            className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="anim-type">
                            <option value="fade">Fade</option>
                            <option value="slide">Slide</option>
                            <option value="zoom">Zoom</option>
                            <option value="none">None</option>
                        </select>
                    </label>
                    <Field label="Duration (ms)" type="number" value={cfg.animation_duration} onChange={(v) => set("animation_duration", parseInt(v) || 700)} testid="anim-duration" />
                </div>
            </Section>

            {/* Donation page */}
            <Section title="Donation Page & Methods" icon={Heart}>
                <Field label="Donation hero title" value={cfg.donate_hero_title} onChange={(v) => set("donate_hero_title", v)} testid="donate-title" />
                <Area label="Donation hero subtitle" value={cfg.donate_hero_subtitle} onChange={(v) => set("donate_hero_subtitle", v)} testid="donate-subtitle" />
                <Area label="Impact text (where the gift goes)" value={cfg.donate_impact_text} onChange={(v) => set("donate_impact_text", v)} rows={4} testid="donate-impact" />
                <div className="border-t border-border pt-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-secondary mb-3">Mobile Money</div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Field label="Provider" value={cfg.donate_mobile_money_provider} onChange={(v) => set("donate_mobile_money_provider", v)} testid="momo-provider" />
                        <Field label="Number" value={cfg.donate_mobile_money_number} onChange={(v) => set("donate_mobile_money_number", v)} testid="momo-number" />
                        <Field label="Account name" value={cfg.donate_mobile_money_name} onChange={(v) => set("donate_mobile_money_name", v)} testid="momo-name" />
                    </div>
                </div>
                <div className="border-t border-border pt-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-secondary mb-3">Bank Transfer</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Bank name" value={cfg.donate_bank_name} onChange={(v) => set("donate_bank_name", v)} testid="bank-name" />
                        <Field label="Account name" value={cfg.donate_bank_account_name} onChange={(v) => set("donate_bank_account_name", v)} testid="bank-account-name" />
                        <Field label="Account number" value={cfg.donate_bank_account_number} onChange={(v) => set("donate_bank_account_number", v)} testid="bank-account-number" />
                        <Field label="SWIFT / BIC" value={cfg.donate_bank_swift} onChange={(v) => set("donate_bank_swift", v)} testid="bank-swift" />
                        <Field label="Branch" value={cfg.donate_bank_branch} onChange={(v) => set("donate_bank_branch", v)} testid="bank-branch" />
                    </div>
                </div>
                <div className="border-t border-border pt-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-secondary mb-3">South Sudanese Pound (SSP) display</div>
                    <Field label="1 USD = ? SSP (used to show SSP equivalents on mobile money)" type="number" value={cfg.donate_ssp_rate} onChange={(v) => set("donate_ssp_rate", parseFloat(v) || 0)} testid="ssp-rate" />
                </div>
                <p className="text-xs text-muted-foreground">Card payments via Stripe support USD, EUR, GBP (selectable by donor). Monthly recurring + one-time both available. SSP is shown alongside on mobile money/bank only.</p>
            </Section>

            <div className="sticky bottom-6 z-10">
                <button onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium disabled:opacity-60 shadow-lg"
                    data-testid="save-settings">
                    <Save size={14} /> {saving ? "Saving…" : "Save & reload"}
                </button>
            </div>
        </div>
    );
}

const Section = ({ title, icon: Icon, children }) => (
    <section className="bg-card border border-border rounded-sm p-8 space-y-5">
        <div className="flex items-center gap-3 mb-4">
            <Icon size={18} className="text-primary" />
            <h2 className="font-display text-2xl">{title}</h2>
        </div>
        {children}
    </section>
);

const LogoUploader = ({ onUpload }) => {
    const [busy, setBusy] = useState(false);
    const upload = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onUpload(data.url);
        } finally { setBusy(false); }
    };
    return (
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer hover:border-primary/60 text-sm whitespace-nowrap" data-testid="logo-upload-button">
            <UploadIcon size={14} /> {busy ? "Uploading…" : "Upload logo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="logo-file-input" />
        </label>
    );
};
